<template>
  <div class="signup-login-wrapper">
    <div class="logo-wrapper">
      <a href="/">
        <GkLogoAnim theme="white" />
      </a>
    </div>
    <div v-if="heroCodeIsValid" class="invitation-by">
      <h1>{{ $gettextInterpolate(uniqueDiscountText, { inviter_name: inviterName }) }}</h1>
      <p v-html="$gettextInterpolate(invitationText, { inviter_name: inviterName, discount_amount: discountAmount, premium_year_cost: premiumYearCost })" />
    </div>
    <div v-else class="invitation-by invalid-invitecode">
      <h1>{{ $gettext('Invalid code') }}</h1>
      <p v-html="$gettext('Sorry, it seems this discount link is no longer active. Please contact the person who provided you with this link or sign up without this link for the free <a href=\'https://www.getgekko.com/\' class=\'text-link\'>Gekko Basic program</a>.')" />
    </div>
    <div v-if="heroCodeIsValid" class="signup-login-form-wrap signup-heroes">
      <div class="discount-badge" v-html="$gettextInterpolate(badgeDiscountText, { discount_amount: discountAmount })" />
      <transition name="fade" appear mode="out-in">
        <SignupFormSimple
          :key="mainTransitionKey"
          @authorised="$emit('authorised', false)"
          :remember-email="rememberEmail"
          :invite-code="(heroCodeIsValid) ? inviteCode : null"
          @remember-email="(email) => { rememberEmail = email }"
        />
      </transition>
    </div>
    <transition name="fade-delayed" appear mode="out-in">
      <div v-if="heroCodeIsValid" class="security">
        <div class="secure-online">
          <font-awesome-icon icon="fa-solid fa-shield-check" />
          <div>{{ $gettext('All your data is securely transmitted from you, and securely stored on, our European servers.') }}</div>
        </div>
        <div class="signup-link-wrap">
          {{ $gettext('Already have an account?') }} <router-link data-analytics-click="signup from login" :to="{ name: 'Login' }" class="signup-link text-blue">{{ $gettext('Sign in') }}</router-link>
        </div>
      </div>
    </transition>
    <transition name="fade-delayed" appear mode="out-in">
      <div v-if="heroCodeIsValid" class="signup-faq">
        <div class="signup-faq__item">
          <h3>{{ $gettext('What kind of signup link is this and why do I get a discount?') }}</h3>
          <p>{{ $gettext('You are about to signup via an invite from a Gekko user who is accepted in the Gekko Heroes program. The Gekko Heroes program allows that person to invite new users like yourself to Gekko and give them a major discount on Gekko Premium. You cannot get this discount in any other way, so feel free to thank your Hero for giving you this oppertunity.') }}</p>
        </div>
        <div class="signup-faq__item">
          <h3>{{ $gettext('So do I need to pay to get access?') }}</h3>
          <p>{{ $gettext('No. This is a free signup link. Also, you can use our Gekko Basic  program unlimited for free. A great and complete accounting program with no strings attached. However, our paid Gekko Premium program does provide users with a lot of benefits. You can see the differences between both programs in the feature overview below. But if you really want to know why to upgrade, please ask the person who send you this invite link. All Gekko Heroes have signed up for Gekko Premium in the past and are using our paid program. So the choice is yours.') }}</p>
        </div>
        <div class="signup-faq__item">
          <h3>{{ $gettext('Is the free account unlimited?') }}</h3>
          <p>{{ $gettext('Yes! The basic plan has everything you need to keep track of your accounting. It is free, gives you access to all the mobile applications and you can use it as long as you want. We hope you\'ll like our premium features enough to upgrade of course but it\'s not needed to enjoy Gekko. Check out the differences below.') }}</p>
        </div>
        <div class="signup-faq__item">
          <h3>{{ $gettext('How much discount will I get if I signup via this link?') }}</h3>
          <p>{{ $gettext('The discount you get by signing up via this link will only be applicable for users that signup for a yearly Gekko Premium subscription. You will get 54 EUR discount (37,5%) and only pay 90 EUR instead of 144 EUR for one year of Gekko Premium. Do keep in mind that this discount is time limited. You need to decide within 20 days after signing up whether you want to use this discount or stick to the free Gekko Basic account. After that time your discount is lost.') }}</p>
        </div>
        <div class="signup-faq__item">
          <h3>{{ $gettext('What\'s your refund policy?') }}</h3>
          <p>{{ $gettext('We do not refund initial payments. After all, we provide you with a free program that you can use to find out exactly what you are buying. So please be careful about upgrading when you are not certain and try our Basic account a while longer. However, we do refund unwanted automatic extensions of you subscription. Just be sure to ask for a refund within 10 days after the payment was automatically credited to your account. We will then refund your last payment in full. If you want to know more about the rules, please check our user agreement for more details.') }}</p>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex"

import GkLogoAnim from "@/components/ui/GkLogoAnim"
import GekkoButton from "@/components/GekkoButton"

import SignupFormSimple from '@/components/signup/SignupFormSimple'

export default {
  name: 'SignupHeroes',
  components: {
    GekkoButton,
    GkLogoAnim,
    SignupFormSimple,
  },
  data () {
    return {
      mainTransitionKey: 0,
      rememberEmail: '',
      inviteCode: null,
      heroCodeIsValid: false,
      inviterName: null,
      discountAmount: '37,5%',
      premiumYearCost: '90 EUR'
    }
  },
  computed: {
    ...mapGetters({ 
      isAuthenticated: "auth/getAuthenticated",
      isNewAccount: "signup/getSignupStatus",
    }),
    uniqueDiscountText () {
      return this.$gettext('Unique discount from %{ inviter_name }')
    },
    invitationText () {
      return this.$gettext('By using this unique discount link from %{ inviter_name } you will get a <strong>%{ discount_amount } discount</strong> to Gekko Premium if you upgrade within 20 days after signing up below for Gekko Basic. That is one year of Premium for <strong>only %{ premium_year_cost }</strong>. Do note: this offer only applies to new users that signup via this link and is time limited from the moment you signup.')
    },
    badgeDiscountText () {
      return this.$gettext('<div>Get</div><div class=\'discount-amount\'>%{ discount_amount }<span>off</span></div>')
    }
  },
  created () {
    this.setupData()
  },
  mounted () {
    
  },
  methods: {
    ...mapActions({ 
      checkHeroCode: 'signup/checkHeroCode'
    }),
    setupData () {
      this.inviteCode = this.$route.params['uuid']

      this.checkHeroCode(this.inviteCode).then((response) => {
        if (response.data) {
          this.heroCodeIsValid = true
          this.inviterName = response.data
        } else {
          this.heroCodeIsValid = false
          this.inviterName = null
        }
      }).catch((error) => {
        console.log('error', error)
      })

      if (this.isAuthenticated) {
        if (this.isNewAccount) {
          this.$router.push({ name: 'Dashboard', query: { welcome: 'yes' }  }).catch(() => {})
        } else {
          this.$router.push({ name: 'Dashboard' }).catch(() => {})
        }
      }
    }
  }
}
</script>

<style lang="scss">
  .signup-login-wrapper {
    .invitation-by {
      .text-link {
        color: white;
        text-decoration: underline;
      }
    }
  }
</style>

<style lang="scss" scoped>
  .signup-login-wrapper {
    max-width: 750px;
    width: 750px;
    .security {
      max-width: 540px;
      margin: 0 auto;
			margin-top: 1rem;
		}
    .invitation-by {
			color: white;
			border-radius: 24px;
      width: 100%;
			border: 2px solid #a5d7c7;
			border-bottom: 4px solid #a5d7c7;

      padding: 2rem;
      &:not(.invalid-invitecode) {
        padding-bottom: 4rem;
      }
			h1 {
        font-family: 'Campton-Bold';
				font-size: 26px;
        margin-bottom: 2rem;
			}
			p {
				margin: 0;
        font-family: 'Campton-Book';
        font-size: 16px;
			}
			h1, p {
				color: white !important;
			}
      @media (min-width: 768px) {
        padding: 3rem;
        &:not(.invalid-invitecode) {
          padding-bottom: 6rem;
        }
        h1 { font-size: 30px; }
        p { font-size: 18px; }
      }
		}
    .signup-heroes {
      max-width: 540px;
      margin: 0 auto;
      margin-top: -3rem;
    }
    .discount-badge {
			position: absolute;
      padding: 1rem;
      top: -70px;
      right: 2rem;
			z-index: 10;
			display: flex;
			align-items: center;
			justify-content: center;
			flex-direction: column;
      width: 100px;
      height: 100px;
			border: 3px dashed white;
			box-shadow: 0 0 0 2px #C42F52, rgba(0,0,0,0.2) 0px 15px 35px 0px, rgba(0,0,0,0.12) 0px 5px 15px 0px;
      background: #C42F52;
			color: white;
			border-radius: 100%;
      font-size: 18px;
			line-height: 1.2;
			zoom: 0.9;
			.discount-amount {
				font-family: 'Campton-Bold';
				text-align: center;
        font-size: 22px;
        span { font-size: 16px; }
			}
      @media (min-width: 768px) {
				width: 150px;
				height: 150px;
        top: -60px;
        right: -60px;
        font-size: 24px;
				.discount-amount {
					font-size: 28px;
          span { font-size: 20px; }
				}
			}
		}
    .signup-faq {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      padding: 3rem 0;
      &__item {
        h3 {
          font-family: 'Campton-Bold', sans-serif;
          font-size: 24px;
          margin-bottom: 1rem;
        }
        p {
          font-family: 'Campton-Book', sans-serif;
          font-size: 1rem;
          margin-bottom: 1rem;
        }
      }
    }
  }
</style>